import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateStageHintDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../translations';

import { HintEditorForm } from './HintEditorForm';

interface HintModalEditorProps {
  id?: string;
  hint: CreateUpdateStageHintDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    hint: CreateUpdateStageHintDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const HintModalEditor: React.FC<HintModalEditorProps> = props => {
  const [hint, setHint] = useState(props.hint);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setHint(props.hint);
    }
  }, [props.hint, props.show]);

  const createHint = async () => {
    setIsLoading(true);
    props
      .onCompleted(hint, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateStageHintDto>) => {
    setHint({ ...hint, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id ? t(tk.timeLine.editHint) : t(tk.timeLine.addHint)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HintEditorForm hint={hint} propertyChange={propertyChange} />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={pok.stageHints.getLastEditor} />
        )}
        <Button
          variant="outline-secondary"
          onClick={props.handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        <Button variant="primary" onClick={createHint} isLoading={isLoading}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
